/**
 * LiveCameraCapture — strict no-upload mobile camera component (iter137.4).
 *
 * Uses the browser's ``getUserMedia`` API to render an in-app camera preview
 * with a single capture button. There is NO file picker fallback — the user
 * must literally point the device camera at the subject and shoot. This
 * matches the field-tech workflow rule: warranty / commissioning / PM
 * service evidence cannot be supplied from the photo library.
 *
 * Modes:
 *   "photo"  — single frame, captured from the live <video> stream.
 *   "video"  — MediaRecorder stops up to ``maxSeconds`` (default 30s).
 *
 * Output is a Blob the caller can upload directly via FormData.
 *
 * Browser support:
 *   • iOS Safari 14.5+ — getUserMedia OK; MediaRecorder is iOS 14.3+.
 *   • Android Chrome — full support.
 *   • Desktop browsers — works for QA but the preview is whatever the
 *     attached webcam streams (rear camera not available on laptops).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, VideoCamera, X, ArrowsClockwise, CheckCircle, Stop } from "@phosphor-icons/react";

const _isMediaSupported = () =>
  typeof navigator !== "undefined" &&
  navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === "function";

export default function LiveCameraCapture({
  mode = "photo",          // "photo" | "video"
  maxSeconds = 30,
  open,
  onClose,
  onCapture,                // (Blob, {mime_type, filename}) => void
  facingMode = "environment", // "environment" (rear) | "user" (front)
  testIdPrefix = "live-cam",
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const recChunksRef = useRef([]);
  const tickRef = useRef(null);
  const [error, setError] = useState("");
  const [facing, setFacing] = useState(facingMode);
  const [recording, setRecording] = useState(false);
  const [recElapsed, setRecElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewBlob, setPreviewBlob] = useState(null);

  // ── Stream lifecycle ────────────────────────────────────────────
  const startStream = useCallback(async () => {
    setError("");
    if (!_isMediaSupported()) {
      setError("Camera access isn't supported in this browser.");
      return;
    }
    try {
      const constraints = {
        audio: mode === "video",
        video: {
          facingMode: { ideal: facing },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        // iOS Safari needs an explicit play() call after srcObject set.
        try { await videoRef.current.play(); } catch { /* noop */ }
      }
    } catch (e) {
      const name = e?.name || "";
      let msg = "Could not access the camera.";
      if (name === "NotAllowedError" || name === "SecurityError") {
        msg = "Camera permission was blocked. Open your browser settings and allow camera access for this site, then retry.";
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        msg = "No camera was found on this device.";
      } else if (name === "NotReadableError") {
        msg = "The camera is already in use by another app. Close it and try again.";
      }
      setError(msg);
    }
  }, [facing, mode]);

  const stopStream = useCallback(() => {
    try { recorderRef.current?.stop(); } catch { /* noop */ }
    recorderRef.current = null;
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setRecording(false);
    setRecElapsed(0);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (open) {
      setPreviewUrl("");
      setPreviewBlob(null);
      startStream();
    } else {
      stopStream();
    }
    return stopStream;
  }, [open, startStream, stopStream]);

  // Restart the stream when the user flips the camera direction.
  const flipCamera = () => {
    setFacing((f) => (f === "environment" ? "user" : "environment"));
  };
  useEffect(() => {
    if (open && streamRef.current) {
      stopStream();
      startStream();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing]);

  // ── Capture ─────────────────────────────────────────────────────

  const capturePhoto = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) { setError("Camera not ready yet."); return; }
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) { setError("Capture failed."); return; }
      setPreviewBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
    }, "image/jpeg", 0.92);
  };

  const startVideo = () => {
    const s = streamRef.current;
    if (!s) { setError("Camera not ready yet."); return; }
    setError("");
    recChunksRef.current = [];
    let mimeType = "";
    // Pick the first widely-supported codec the browser admits.
    for (const m of ["video/mp4;codecs=h264,aac", "video/webm;codecs=vp9,opus", "video/webm"]) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) {
        mimeType = m;
        break;
      }
    }
    let rec;
    try {
      rec = mimeType ? new MediaRecorder(s, { mimeType }) : new MediaRecorder(s);
    } catch (e) {
      setError(`Recorder failed to start: ${e?.message || e}`);
      return;
    }
    rec.ondataavailable = (ev) => {
      if (ev.data && ev.data.size) recChunksRef.current.push(ev.data);
    };
    rec.onstop = () => {
      const blob = new Blob(recChunksRef.current, { type: mimeType || "video/webm" });
      setPreviewBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
      setRecording(false);
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    };
    recorderRef.current = rec;
    rec.start();
    setRecording(true);
    setRecElapsed(0);
    tickRef.current = setInterval(() => {
      setRecElapsed((s2) => {
        const next = s2 + 1;
        if (next >= maxSeconds) {
          try { rec.stop(); } catch { /* noop */ }
        }
        return next;
      });
    }, 1000);
  };

  const stopVideo = () => {
    try { recorderRef.current?.stop(); } catch { /* noop */ }
  };

  const acceptPreview = () => {
    if (!previewBlob) return;
    const ext = (previewBlob.type.split("/")[1] || "").split(";")[0]
      .replace("jpeg", "jpg").replace("quicktime", "mov");
    const filename = `live_${Date.now()}.${ext || (mode === "video" ? "webm" : "jpg")}`;
    onCapture?.(previewBlob, { mime_type: previewBlob.type, filename });
    URL.revokeObjectURL(previewUrl);
    setPreviewBlob(null);
    setPreviewUrl("");
    onClose?.();
  };

  const retake = () => {
    URL.revokeObjectURL(previewUrl);
    setPreviewBlob(null);
    setPreviewUrl("");
    if (!streamRef.current) startStream();
  };

  if (!open) return null;

  return (
    <div
      data-testid={`${testIdPrefix}-modal`}
      className="fixed inset-0 z-[200] bg-black flex flex-col"
    >
      <div className="flex items-center justify-between px-3 py-2 bg-black/80 text-white">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
          {mode === "video" ? <VideoCamera size={14} weight="bold" /> : <Camera size={14} weight="bold" />}
          {mode === "video" ? "Live video" : "Live photo"}
          {recording && (
            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-rose-600 animate-pulse">
              REC {recElapsed}s
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`${testIdPrefix}-flip`}
            onClick={flipCamera}
            className="p-2 rounded-sm hover:bg-white/10"
            title="Flip camera"
          >
            <ArrowsClockwise size={18} weight="bold" />
          </button>
          <button
            type="button"
            data-testid={`${testIdPrefix}-close`}
            onClick={() => { stopStream(); onClose?.(); }}
            className="p-2 rounded-sm hover:bg-white/10"
            title="Close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
      </div>

      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {error && (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-6 text-center bg-black/85 text-white text-sm">
            <div>
              <div className="font-bold mb-2">Camera unavailable</div>
              <div className="text-slate-300 max-w-xs">{error}</div>
              <button
                type="button"
                onClick={startStream}
                className="mt-3 px-3 py-1.5 bg-white text-black text-xs font-bold rounded-sm"
              >
                Retry
              </button>
            </div>
          </div>
        )}
        {!previewUrl && (
          <video
            ref={videoRef}
            data-testid={`${testIdPrefix}-preview`}
            autoPlay
            playsInline
            muted
            className="max-h-full max-w-full object-contain"
          />
        )}
        {previewUrl && mode === "photo" && (
          <img src={previewUrl} alt="captured" className="max-h-full max-w-full object-contain" />
        )}
        {previewUrl && mode === "video" && (
          <video src={previewUrl} controls playsInline className="max-h-full max-w-full" />
        )}
      </div>

      <div className="bg-black/90 px-3 py-3 flex items-center justify-center gap-3">
        {!previewUrl && mode === "photo" && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-shutter`}
            onClick={capturePhoto}
            className="h-16 w-16 rounded-full bg-white border-4 border-slate-300 active:scale-95 transition"
            aria-label="Capture photo"
          />
        )}
        {!previewUrl && mode === "video" && (
          <>
            {!recording ? (
              <button
                type="button"
                data-testid={`${testIdPrefix}-start`}
                onClick={startVideo}
                className="h-16 w-16 rounded-full bg-rose-600 hover:bg-rose-700 border-4 border-white active:scale-95 transition"
                aria-label="Start recording"
              />
            ) : (
              <button
                type="button"
                data-testid={`${testIdPrefix}-stop`}
                onClick={stopVideo}
                className="h-16 w-16 rounded-full bg-white border-4 border-rose-600 flex items-center justify-center active:scale-95 transition"
                aria-label="Stop recording"
              >
                <Stop size={26} weight="fill" className="text-rose-600" />
              </button>
            )}
          </>
        )}
        {previewUrl && (
          <>
            <button
              type="button"
              data-testid={`${testIdPrefix}-retake`}
              onClick={retake}
              className="px-4 py-2 rounded-sm bg-slate-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-slate-600"
            >
              Retake
            </button>
            <button
              type="button"
              data-testid={`${testIdPrefix}-accept`}
              onClick={acceptPreview}
              className="px-4 py-2 rounded-sm bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 inline-flex items-center gap-1.5"
            >
              <CheckCircle size={14} weight="bold" /> Use this {mode}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
